import { checkPermissions } from "@elit-hair/elit-tools";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, MenuList, Typography } from "@mui/material";
import { GridMoreVertIcon, GridRowSelectionModel } from "@mui/x-data-grid";
import { MouseEvent, createElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { PassengerStatusComp, passengerStatuses } from "@/constants/passengerStatuses";
import { PERMISSIONS } from "@/constants/permissions";
import { PASSENGER_STATUS } from "@/constants/status";
import useAuth from "@/providers/hooks/useAuth";
import { IPlanPassenger } from "@/types/tasks.types";

import { ShowStatusConfirmation } from "..";

const findStatus = (status: PASSENGER_STATUS) => passengerStatuses.find((item) => item.status === status) || {};

function WelcomeTeamActions({
    items,
    selectionList,
    handleStatusConfirmation,
}: {
    handleStatusConfirmation: (val?: ShowStatusConfirmation) => void;
    selectionList: GridRowSelectionModel;
    items: IPlanPassenger<"api">[];
}) {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const open = Boolean(anchorEl);
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const { userData } = useAuth();
    const hasEditPermission = checkPermissions(userData?.permissionCodes, [
        PERMISSIONS.TR_SVC_WELCOME_TEAM_PASSENGERS_SET_DATA,
    ]);

    const totals = items.reduce<{
        [key in PASSENGER_STATUS]: PassengerStatusComp & { count: number; targetStatus: PASSENGER_STATUS };
    }>(
        (prev, curr) => {
            if (!selectionList.includes(curr.id)) return prev;

            if (curr.status && prev[curr.status]) prev[curr.status].count += 1;
            return prev;
        },
        {
            [PASSENGER_STATUS.PENDING]: {
                targetStatus: PASSENGER_STATUS.WAITING,
                ...findStatus(PASSENGER_STATUS.WAITING),
                count: 0,
            },
            [PASSENGER_STATUS.WAITING]: {
                targetStatus: PASSENGER_STATUS.PENDING,
                ...findStatus(PASSENGER_STATUS.PENDING),
                count: 0,
            },
        } as { [key in PASSENGER_STATUS]: PassengerStatusComp & { count: number; targetStatus: PASSENGER_STATUS } }
    );

    return (
        <>
            {hasEditPermission && (
                <>
                    <IconButton
                        aria-label="more"
                        id="long-button"
                        aria-controls={open ? "long-menu" : undefined}
                        aria-expanded={open ? "true" : undefined}
                        aria-haspopup="true"
                        className=" !p-0"
                        onClick={handleClick}
                    >
                        <GridMoreVertIcon />
                    </IconButton>
                    <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                            "aria-labelledby": "basic-button",
                        }}
                    >
                        <MenuList>
                            {[PASSENGER_STATUS.PENDING, PASSENGER_STATUS.WAITING].map((status) => (
                                <MenuItem
                                    disabled={totals[status]?.count <= 0}
                                    key={status}
                                    onClick={() => {
                                        if (totals[status].targetStatus)
                                            handleStatusConfirmation([
                                                status as Exclude<PASSENGER_STATUS, "PICKED_UP" | "DONE">,
                                                totals[status].targetStatus,
                                            ]);
                                        handleClose();
                                    }}
                                >
                                    <ListItemIcon>
                                        {totals[status] &&
                                            createElement(totals[status].component(), {
                                                color: totals[status].hover,
                                            })}
                                    </ListItemIcon>
                                    <ListItemText>
                                        {t("passengerStatuses." + ((totals[status] && totals[status].status) || ""))}
                                    </ListItemText>
                                    <Typography variant="body2" sx={{ color: "text.secondary", paddingLeft: 3 }}>
                                        {totals[status] && totals[status].count}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </MenuList>
                    </Menu>
                </>
            )}
        </>
    );
}

export default WelcomeTeamActions;
